<script setup lang="ts">
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import type { FetchDataOptions, PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { Edit, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-vue-next'
  import { Badge } from '@/components/ui/badge'
  import { useTranslation } from '@/composables/useTranslation'
  import type { DocumentPlaceholder } from '@/types/generated'
  import ApiService from '@/services/api.service'

  const { t } = useTranslation({
    en: {
      actions: 'Actions',
      createdAt: 'Created At',
      placeholderName: 'Placeholder Name',
      germanLabel: 'German Label',
      englishLabel: 'English Label',
      usedInTemplates: 'Used In Templates',
      missing: 'Missing',
      templates: 'templates',
      edit: 'Edit',
      placeholderManagement: 'Placeholder Management'
    },
    de: {
      actions: 'Aktionen',
      createdAt: 'Erstellt am',
      placeholderName: 'Platzhalter Name',
      germanLabel: 'Deutsche Bezeichnung',
      englishLabel: 'Englische Bezeichnung',
      usedInTemplates: 'In Vorlagen verwendet',
      missing: 'Fehlend',
      templates: 'Vorlagen',
      edit: 'Bearbeiten',
      placeholderManagement: 'Platzhalter-Verwaltung'
    }
  })

  const router = useRouter()

  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)
  const sortField = ref<string | null>(null)
  const sortOrder = ref<'asc' | 'desc'>('asc')

  const fetchPlaceholders = async (options?: FetchDataOptions): Promise<PaginatedResponse<any>> => {
    try {
      const params: any = {}

      if (options?.limit) {
        params.limit = options.limit
      }
      if (options?.offset) {
        params.offset = options.offset
      }
      if (options?.search) {
        params.search = options.search
      }
      if (sortField.value) {
        params.ordering = sortOrder.value === 'desc' ? `-${sortField.value}` : sortField.value
      }

      const response = await ApiService.get('/document_placeholders/', { params }, '/v3')

      return {
        results: response.data.results || response.data || [],
        next: response.data.next || null,
        previous: response.data.previous || null,
        count: response.data.count || response.data.results?.length || response.data?.length || 0
      }
    } catch (error) {
      console.error('Error fetching placeholders:', error)
      return {
        results: [],
        next: null,
        previous: null,
        count: 0
      }
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const navigateToDetail = (placeholder: DocumentPlaceholder) => {
    router.push(ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDER_DETAIL.replace(':uuid', placeholder.uuid))
  }

  const handleSort = (field: string) => {
    if (sortField.value === field) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    } else {
      sortField.value = field
      sortOrder.value = 'asc'
    }
    paginatedTableRef.value?.refreshData()
  }

  const getSortIcon = (field: string) => {
    if (sortField.value !== field) return ArrowUpDown
    return sortOrder.value === 'asc' ? ArrowUp : ArrowDown
  }

  const isSorted = (field: string) => sortField.value === field
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Word templates', to: ROUTES.ADMIN.WORD_TEMPLATES },
            { title: 'Placeholders', to: ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDERS }
          ]"
        />
      </div>
      <div
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <PaginatedTableView
          ref="paginatedTableRef"
          :fetchData="fetchPlaceholders"
          :fetch-options="{ tag: 'MASTER' }"
          searchableFields="placeholder name, german label, english label"
        >
          <template #table-head>
            <TableCell>UUID</TableCell>
            <TableCell>
              <button
                class="flex items-center gap-1 font-medium transition-colors hover:text-purple-600"
                :class="{ 'text-purple-600': isSorted('placeholder_name') }"
                @click="handleSort('placeholder_name')"
              >
                {{ t('placeholderName') }}
                <component
                  :is="getSortIcon('placeholder_name')"
                  class="h-4 w-4"
                />
              </button>
            </TableCell>
            <TableCell>{{ t('germanLabel') }}</TableCell>
            <TableCell>{{ t('englishLabel') }}</TableCell>
            <TableCell>
              <button
                class="flex items-center gap-1 font-medium transition-colors hover:text-purple-600"
                :class="{ 'text-purple-600': isSorted('template_count') }"
                @click="handleSort('template_count')"
              >
                {{ t('usedInTemplates') }}
                <component
                  :is="getSortIcon('template_count')"
                  class="h-4 w-4"
                />
              </button>
            </TableCell>
            <TableCell>
              <button
                class="flex items-center gap-1 font-medium transition-colors hover:text-purple-600"
                :class="{ 'text-purple-600': isSorted('created_at') }"
                @click="handleSort('created_at')"
              >
                {{ t('createdAt') }}
                <component
                  :is="getSortIcon('created_at')"
                  class="h-4 w-4"
                />
              </button>
            </TableCell>
            <TableCell>{{ t('actions') }}</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="placeholder in items"
              :key="placeholder.uuid"
            >
              <UniqueTableDataCell :truncate="true">
                <router-link
                  :to="
                    ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDER_DETAIL.replace(
                      ':uuid',
                      placeholder.uuid
                    )
                  "
                >
                  {{ placeholder.uuid }}
                </router-link>
              </UniqueTableDataCell>
              <TableCell class="font-mono text-sm">
                {{ placeholder.placeholder_name }}
              </TableCell>
              <TableCell>
                <Badge
                  v-if="!placeholder.german_label"
                  variant="outline"
                  class="text-orange-600"
                >
                  {{ t('missing') }}
                </Badge>
                <span v-else>{{ placeholder.german_label }}</span>
              </TableCell>
              <TableCell>
                <Badge
                  v-if="!placeholder.english_label"
                  variant="outline"
                  class="text-orange-600"
                >
                  {{ t('missing') }}
                </Badge>
                <span v-else>{{ placeholder.english_label }}</span>
              </TableCell>
              <TableCell>
                <Badge
                  variant="secondary"
                  class="text-nowrap"
                >
                  {{ placeholder.template_count || 0 }} {{ t('templates') }}
                </Badge>
              </TableCell>
              <TableCell>
                {{ placeholder.created_at ? formatDate(placeholder.created_at) : '-' }}
              </TableCell>
              <TableCell>
                <TableOptionsButton
                  :options="[
                    {
                      label: t('edit'),
                      icon: Edit,
                      onClick: () => navigateToDetail(placeholder)
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
  </div>
</template>
